import { z } from 'zod';

/**
 * Shared wire-layer scalars. Grounded in docs/05a-api-baseline.md:
 * responses are raw Eloquent serialization with per-controller envelopes —
 * there is NO global envelope, so every endpoint gets its own schema.
 */

/**
 * Decimal columns (`price`, `total_amount`, `delivery_charges`, …) serialize
 * as strings ("550.00"). Keep them strings at the wire layer; parse to number
 * only where a store/component does arithmetic. Tolerates a plain number in
 * case a handler bypasses the decimal cast.
 */
export const wireDecimal = z.union([z.string(), z.number()]).transform((v) => String(v));
export type DecimalString = string;

/** ISO-8601 with microseconds: "2026-07-31T09:00:00.000000Z". Kept as string. */
export const isoDateTime = z.string();

/**
 * Boolean-ish for columns WITHOUT an Eloquent boolean cast, which serialize
 * as 0/1 (e.g. branches.is_active — pos-api/app/Models/Branch.php:35-40 casts
 * delivery/pickup/disable_ordering but not is_active).
 */
export const looseBoolean = z
  .union([z.boolean(), z.number(), z.string()])
  .transform((v) => v === true || v === 1 || v === '1');

/** Integer that may arrive as a numeric string (paginator per_page echoes the query param). */
export const intish = z.union([z.number(), z.string().regex(/^\d+$/).transform(Number)]);

/**
 * Raw Laravel paginator (categories/branches/orders/customers indexes):
 * { current_page, data, first_page_url, from, last_page, links, next_page_url,
 *   path, per_page, prev_page_url, to, total }. Unknown keys pass through.
 */
export function laravelPaginator<T extends z.ZodType>(item: T) {
  return z.looseObject({
    current_page: z.number(),
    data: z.array(item),
    last_page: z.number(),
    per_page: intish,
    total: z.number(),
    from: z.number().nullish(),
    to: z.number().nullish(),
  });
}

/** Normalized pagination for internal consumption. */
export interface Paginated<T> {
  items: T[];
  page: number;
  lastPage: number;
  perPage: number;
  total: number;
}
